import { SPECIFICATION_IMAGE_FILE } from "../../constants/imageGeneratorDictionaries";
import { getImageLink } from "../../utils/commonUtils";
import { admin } from "./axios";
import {
  getCachedImageGeneratorCatalog,
  imageGeneratorCatalogCacheKey,
} from "./imageGeneratorCatalogCache";

export type ImageGeneratorCatalogSwatch = {
  hex: string;
  tier: "major" | "minor";
  /** Extracted pantone code when present; empty hex means lookup miss. */
  pantone?: string | null;
};

export type ImageGeneratorCatalogAsset = {
  stem: string;
  available: boolean;
  url: string;
};

/** Editorial page from `reports.slider_image_urls` (View report). */
export type ImageGeneratorReportPage = {
  id: string;
  name: string;
  url: string;
  sliderImageIndex: number | null;
};

export type ImageGeneratorCatalogColourStory = {
  id: string;
  title: string;
  palette: ImageGeneratorCatalogSwatch[];
  sliderImageIndex?: number | null;
  sliderImageUrl?: string | null;
  pages?: ImageGeneratorReportPage[];
};

export type ImageGeneratorCatalogPatternTrend = {
  id: string;
  name: string;
  assets: ImageGeneratorCatalogAsset[];
  sliderImageIndex?: number | null;
  sliderImageUrl?: string | null;
  pages?: ImageGeneratorReportPage[];
};

export type ImageGeneratorCatalogMicroTheme = {
  id: string;
  name: string;
  patternTrends: ImageGeneratorCatalogPatternTrend[];
  pages?: ImageGeneratorReportPage[];
};

export type ImageGeneratorCatalogMacro = {
  id: string;
  name: string;
  colourStories: ImageGeneratorCatalogColourStory[];
  patternTrends: ImageGeneratorCatalogPatternTrend[];
  /** Western micro reports (Pastoral / Amuse). Not the same list as patternTrends. */
  microThemes?: ImageGeneratorCatalogMicroTheme[];
  pages?: ImageGeneratorReportPage[];
};

/** Capsules share the macro catalogue shape for palettes / micro-themes / tiles. */
export type ImageGeneratorCatalogCapsule = ImageGeneratorCatalogMacro;

export type ImageGeneratorCatalog = {
  season: {
    id: string;
    label: string;
  };
  macros: ImageGeneratorCatalogMacro[];
  capsules: ImageGeneratorCatalogCapsule[];
};

export type ImageGeneratorCatalogSurfaceSlide = {
  id: string;
  name: string;
  text: string;
  sliderImageIndex: number | null;
  image: {
    available: boolean;
    url: string;
  };
  reportId: number;
  reportTitle: string;
};

export type ImageGeneratorSurfacesCatalog = {
  season: {
    id: string;
    label: string;
  };
  surfaces: ImageGeneratorCatalogSurfaceSlide[];
};

export type ImageGeneratorPatternChoice = "choose" | "recommend" | "none";

export type ImageGeneratorLookView = "front" | "back" | "single";

export type ImageGeneratorFeedbackReason =
  | "incorrect"
  | "blur"
  | "style"
  | "other";

export type ImageGeneratorReview = {
  likes?: Partial<Record<ImageGeneratorLookView, boolean>>;
  feedback?: Array<{
    view: ImageGeneratorLookView;
    reason: ImageGeneratorFeedbackReason;
    details?: string;
    at: string;
  }>;
  approvedAt?: string | null;
  library?: {
    fileName: string;
    name: string;
    savedAt: string;
  } | null;
};

export type ImageGeneratorGenerationStatus =
  | "assembled"
  | "generating"
  | "generated"
  | "failed";

export type ImageGeneratorDesignSheetSlot = {
  id: string;
  fileName: string;
  url: string;
  /** Structured SVG when engine is svg_hybrid (editable download). */
  svgUrl?: string;
};

export type ImageGeneratorDesignSheet = {
  status: "generating" | "generated" | "failed";
  type: "denim" | "macrotheme" | "pattern";
  surfaceTechnique?: "print" | "embroidery";
  engine?: "svg_hybrid" | "ai" | "compose";
  slots: ImageGeneratorDesignSheetSlot[];
  errorMessage?: string;
  errorCode?: string;
  /** When false, FE should not offer retry. Omitted = retryable. */
  retryable?: boolean;
  startedAt: string;
  finishedAt?: string;
};

export type ImageGeneratorGenerateBasePayload = {
  season: string;
  seasonId?: string;
  genre: string;
  market: string;
  category: string;
  macroThemeId: string;
  colourStoryId?: string;
  patternTrendId: string;
  patternChoice: ImageGeneratorPatternChoice;
  selectedAssetStems: string[];
  /** Extracted surface slide id — when set, used instead of pattern trend/tile. */
  surfaceId?: string;
  paletteHexes?: string[];
  garmentFormat?: string;
  embroideryType?: string;
  additionalPrompt?: string;
  patternRepeat?: string; // multi: "Placement, Engineered"
  patternFollowUp?: string; // per subtype: "Placement: Chest; Engineered: Yoke"
  patternFollowUpText?: string; // per subtype free text: "Panel: note"
  patternOffset?: string;
  patternDensity?: string;
  patternScale?: string;
  patternColorCount?: string;
  patternProcess?: string;
};

export type ImageGeneratorGenerateStylePayload =
  ImageGeneratorGenerateBasePayload & {
    productType?: string;
    influence?: string;
    styleType?: string;
    material?: string;
    sleeve?: string;
    neckline?: string;
    fit?: string;
    wash?: string;
    length?: string;
    specificationImageUrl?: string;
    startingDirection?: "macro-theme" | "denim" | "";
    denimMaterialId?: string;
    denimWashId?: string;
    skipReportThemes?: boolean;
    forecastSilhouetteId?: string;
    forecastDetailId?: string;
  };

export type ImageGeneratorGenerateResult = {
  id?: number;
  createdAt?: string;
  outputType: "pattern" | "style";
  status: ImageGeneratorGenerationStatus;
  season: string;
  prompt: string;
  model?: string | null;
  references: {
    macroTheme: string | null;
    colourStory: string | null;
    patternTrend: string | null;
    palette: string[];
    assets: Array<{
      stem: string;
      attached: boolean;
      relativeKey?: string | null;
      url?: string | null;
    }>;
    silhouette: string | null;
  };
  imageUrl: string | null;
  backImageUrl?: string | null;
  sourceGenerationId?: number | null;
  requestPayload?: ImageGeneratorGenerateStylePayload;
  review?: ImageGeneratorReview;
  details?: {
    genre: string;
    season: string;
    market: string;
    category: string;
    outputType: "Style" | "Pattern";
    productType?: string;
    influence?: string;
    styleType?: string;
    material?: string;
    sleeve?: string;
    neckline?: string;
    fit?: string;
    wash?: string;
    length?: string;
    startingDirection?: "macro-theme" | "denim" | "";
    denimMaterialId?: string;
    denimWashId?: string;
    denimMaterial?: string;
    denimWash?: string;
    macroTheme?: string;
    patternChoice: ImageGeneratorPatternChoice;
    surfaceId?: string;
    microTheme?: string;
    garmentFormat?: string;
    embroideryType?: string;
    embroideryPlacement?: string;
    patternRepeat?: string;
    patternFollowUp?: string;
    patternFollowUpText?: string;
    patternOffset?: string;
    patternDensity?: string;
    patternScale?: string;
    patternColorCount?: string;
    patternProcess?: string;
    paletteHexes: string[];
    paletteLabels?: string[];
    palettePantones?: Array<string | null>;
    colourStory?: string;
    additionalPrompt?: string;
    preferences: Array<{ stem: string; url: string }>;
    errorMessage?: string;
    errorCode?: string;
    /** When false, Retry is hidden (e.g. platform credits). Omitted/true → retryable. */
    retryable?: boolean;
    designSheet?: ImageGeneratorDesignSheet;
  };
};

export type ImageGeneratorHistoryRecord = ImageGeneratorGenerateResult & {
  id: number;
  createdAt: string;
};

export type ImageGeneratorHistoryList = {
  items: ImageGeneratorHistoryRecord[];
  total: number;
  limit: number;
  offset: number;
};

export const getImageGeneratorErrorMessage = (
  error: unknown,
  fallback: string
) => {
  const message = (error as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  return message || fallback;
};

export type ImageGeneratorDenimPalette = {
  id: string;
  label: string;
  hexes: string[];
  colourStoryId: string;
};

export type ImageGeneratorDenimTile = {
  id: string;
  label: string;
  kind: "materials" | "washes";
  url: string;
};

export type ImageGeneratorDenimCatalog = {
  palettes: ImageGeneratorDenimPalette[];
  materials: ImageGeneratorDenimTile[];
  washes: ImageGeneratorDenimTile[];
};

export const getImageGeneratorDenimCatalog = async () => {
  const key = imageGeneratorCatalogCacheKey("denim-catalog", {});
  return getCachedImageGeneratorCatalog(key, async () => {
    const response = await admin.get("image-generator/denim-catalog");
    return response.data?.data as ImageGeneratorDenimCatalog;
  });
};

export const getImageGeneratorCatalog = async (params: {
  season: string;
  seasonId?: string;
  market: string;
  category: string;
  genre?: string;
}) => {
  const key = imageGeneratorCatalogCacheKey("catalog", {
    season: params.season,
    seasonId: params.seasonId || "",
    market: params.market,
    category: params.category,
    genre: params.genre || "",
  });
  return getCachedImageGeneratorCatalog(key, async () => {
    const response = await admin.get("image-generator/catalog", { params });
    const data = (response.data?.data || {}) as ImageGeneratorCatalog;
    return {
      ...data,
      macros: data.macros || [],
      capsules: data.capsules || [],
    } satisfies ImageGeneratorCatalog;
  });
};

export const getImageGeneratorSurfacesCatalog = async (params: {
  season: string;
  seasonId?: string;
  market: string;
  category: string;
  genre?: string;
}) => {
  const key = imageGeneratorCatalogCacheKey("surfaces-catalog", {
    season: params.season,
    seasonId: params.seasonId || "",
    market: params.market,
    category: params.category,
    genre: params.genre || "",
  });
  return getCachedImageGeneratorCatalog(key, async () => {
    const response = await admin.get("image-generator/surfaces-catalog", {
      params,
    });
    return response.data?.data as ImageGeneratorSurfacesCatalog;
  });
};

export type ImageGeneratorForecastReports = {
  season: {
    id: string;
    label: string;
  };
  silhouette: ImageGeneratorCatalogSurfaceSlide[];
  detailed: ImageGeneratorCatalogSurfaceSlide[];
};

export const getImageGeneratorForecastReports = async (params: {
  season: string;
  seasonId?: string;
  market: string;
  category: string;
  genre?: string;
}) => {
  const key = imageGeneratorCatalogCacheKey("forecast-reports", {
    season: params.season,
    seasonId: params.seasonId || "",
    market: params.market,
    category: params.category,
    genre: params.genre || "",
  });
  return getCachedImageGeneratorCatalog(key, async () => {
    const response = await admin.get("image-generator/forecast-reports", {
      params,
    });
    const data = (response.data?.data || {}) as ImageGeneratorForecastReports;
    return {
      season: data.season,
      silhouette: data.silhouette || [],
      detailed: data.detailed || [],
    } satisfies ImageGeneratorForecastReports;
  });
};

export type ImageGeneratorSeason = {
  id: string;
  label: string;
};

export const getImageGeneratorSeasons = async (
  market: string,
  category: string
) => {
  const key = imageGeneratorCatalogCacheKey("seasons", { market, category });
  return getCachedImageGeneratorCatalog(key, async () => {
    const response = await admin.get("image-generator/seasons", {
      params: { market, category },
    });
    return (response.data?.data?.results || []) as ImageGeneratorSeason[];
  });
};

/**
 * Prefer CloudFront for generative outputs so tracker/gallery <img> can skip
 * the auth blob proxy. Rewrites S3 `.com` generative URLs via getImageLink;
 * maps relative IG generated API paths / `generative/` keys onto
 * REACT_APP_CLOUD_FRONT_URL when set. Non-generative / private API paths
 * are left unchanged for blob fallback.
 */
export const preferGenerativeCdnUrl = (url: string) => {
  const value = (url || "").trim();
  if (!value) {
    return value;
  }
  if (value.startsWith("data:") || value.startsWith("blob:")) {
    return value;
  }

  // Absolute URLs: rewrite generative S3 → CloudFront when applicable.
  if (/^https?:\/\//i.test(value)) {
    if (/\/generative\//i.test(value)) {
      return getImageLink(value);
    }
    return value;
  }

  const cloudFrontUrl = (process.env.REACT_APP_CLOUD_FRONT_URL ?? "")
    .trim()
    .replace(/\/$/, "");
  if (!cloudFrontUrl) {
    return value;
  }

  let key = value.replace(/^\//, "");
  // Auth proxy path for IG generated outputs → public object key.
  if (key.startsWith("image-generator/generated/")) {
    key = `generative/${key}`;
  }
  if (!/^generative\//i.test(key)) {
    return value;
  }
  return `${cloudFrontUrl}/${key}`;
};

export const getImageGeneratorAssetPreview = async (url: string) => {
  const value = (url || "").trim();
  // CDN / absolute URLs load directly in <img> — no auth blob proxy round-trip.
  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }
  const path = value.replace(/^\//, "");
  const response = await admin.get(path, { responseType: "blob" });
  return URL.createObjectURL(response.data);
};

export const resolveImageGeneratorResultSrc = async (url: string) => {
  const value = preferGenerativeCdnUrl(url);
  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }
  return getImageGeneratorAssetPreview(value);
};

const ENQUEUE_TIMEOUT_MS = 45000;

export const generateImageGeneratorPattern = async (
  payload: ImageGeneratorGenerateBasePayload
) => {
  const response = await admin.post(
    "image-generator/generate/pattern",
    payload,
    { timeout: ENQUEUE_TIMEOUT_MS }
  );
  return response.data?.data as ImageGeneratorGenerateResult;
};

export const generateImageGeneratorStyle = async (
  payload: ImageGeneratorGenerateStylePayload
) => {
  const response = await admin.post("image-generator/generate/style", payload, {
    timeout: ENQUEUE_TIMEOUT_MS,
  });
  return response.data?.data as ImageGeneratorGenerateResult;
};

export const getImageGeneratorHistory = async (limit = 40, offset = 0) => {
  const response = await admin.get("image-generator/history", {
    params: { limit, offset },
  });
  return response.data?.data as ImageGeneratorHistoryList;
};

export const getImageGeneratorLibrary = async (limit = 40, offset = 0) => {
  const response = await admin.get("image-generator/library", {
    params: { limit, offset },
  });
  return response.data?.data as ImageGeneratorHistoryList;
};

export const getImageGeneratorGeneration = async (id: number) => {
  const response = await admin.get(`image-generator/history/${id}`);
  return response.data?.data as ImageGeneratorHistoryRecord;
};

export const isImageGeneratorNotFound = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status === 404;

export type ImageGeneratorRegenView = "front" | "back";

export type ImageGeneratorRegenPayload = {
  additionalPrompt?: string;
  views?: ImageGeneratorRegenView[];
};

export const regenerateImageGenerator = async (
  generationId: number,
  payload: ImageGeneratorRegenPayload = {}
) => {
  const response = await admin.post(
    "image-generator/generate/regen",
    { generationId, ...payload },
    { timeout: ENQUEUE_TIMEOUT_MS }
  );
  return response.data?.data as ImageGeneratorGenerateResult;
};

export const setImageGeneratorLike = async (
  generationId: number,
  view: ImageGeneratorLookView,
  liked: boolean | null
) => {
  const response = await admin.post(
    `image-generator/history/${generationId}/like`,
    { view, liked }
  );
  return response.data?.data as ImageGeneratorHistoryRecord;
};

export const submitImageGeneratorFeedback = async (
  generationId: number,
  payload: {
    view: ImageGeneratorLookView;
    reason: ImageGeneratorFeedbackReason;
    details?: string;
  }
) => {
  const response = await admin.post(
    `image-generator/history/${generationId}/feedback`,
    payload
  );
  return response.data?.data as ImageGeneratorHistoryRecord;
};

export const approveImageGenerator = async (generationId: number) => {
  const response = await admin.post(
    `image-generator/history/${generationId}/approve`
  );
  return response.data?.data as ImageGeneratorHistoryRecord;
};

export const saveImageGeneratorToLibrary = async (
  generationId: number,
  payload: { fileName: string; name: string }
) => {
  const response = await admin.post(
    `image-generator/history/${generationId}/library`,
    payload
  );
  return response.data?.data as ImageGeneratorHistoryRecord;
};

export const startImageGeneratorDesignSheet = async (generationId: number) => {
  const response = await admin.post(
    `image-generator/history/${generationId}/design-sheet`,
    {},
    { timeout: ENQUEUE_TIMEOUT_MS }
  );
  return response.data?.data as ImageGeneratorHistoryRecord;
};

const parseAttachmentFileName = (
  header: string | undefined,
  fallback: string
) => {
  if (!header) {
    return fallback;
  }
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      return utf8[1].trim();
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain?.[1]?.trim() || fallback;
};

/** Download all ready design-sheet slides as one multi-page PDF. */
export const downloadImageGeneratorDesignSheetPdf = async (
  generationId: number
) => {
  const response = await admin.get(
    `image-generator/history/${generationId}/design-sheet/pdf`,
    {
      responseType: "blob",
      timeout: 120000,
    }
  );
  const fileName = parseAttachmentFileName(
    response.headers?.["content-disposition"] as string | undefined,
    `ICH-${generationId}-design-sheet.pdf`
  );
  const objectUrl = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
};

export type ImageGeneratorExtractedSwatch = {
  hex: string;
  name: string;
  accuracy: number;
  tier: "major" | "minor";
  pantone: string | null;
};

export type ImageGeneratorExtractPaletteResult = {
  extractType: "color";
  accuracy: number;
  hexes: string[];
  swatches: ImageGeneratorExtractedSwatch[];
};

export const extractImageGeneratorPalette = async (file: File) => {
  const form = new FormData();
  form.append("image", file);
  const response = await admin.post("image-generator/extract/palette", form, {
    timeout: 60000,
    transformRequest: [
      (data, headers) => {
        if (headers) {
          delete headers["Content-Type"];
          delete (headers as { common?: { "Content-Type"?: string } }).common?.[
            "Content-Type"
          ];
        }
        return data;
      },
    ],
  });
  return response.data?.data as ImageGeneratorExtractPaletteResult;
};

const specificationPathName = (raw: string) => {
  const value = (raw || "").trim().replace(/^\/+/, "");
  const prefix = "image-generator/specifications/";
  if (!value.startsWith(prefix)) {
    return "";
  }
  const fileName = value.slice(prefix.length);
  if (fileName.includes("/") || !SPECIFICATION_IMAGE_FILE.test(fileName)) {
    return "";
  }
  return `${prefix}${fileName.toLowerCase()}`;
};

/** Authenticated API path, or an http(s) URL whose path is one of our specification keys. */
export const isImageGeneratorSpecificationReference = (raw: string) => {
  const value = (raw || "").trim();
  if (!value || value.startsWith("blob:") || value.startsWith("data:")) {
    return false;
  }
  if (specificationPathName(value)) {
    return true;
  }
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return false;
    }
    if (parsed.username || parsed.password) {
      return false;
    }
    return Boolean(specificationPathName(decodeURIComponent(parsed.pathname)));
  } catch {
    return false;
  }
};

export const uploadImageGeneratorSpecification = async (file: File) => {
  const form = new FormData();
  form.append("image", file);
  const response = await admin.post(
    "image-generator/specification-image",
    form,
    {
      timeout: 60000,
      transformRequest: [
        (data, headers) => {
          if (headers) {
            delete headers["Content-Type"];
            delete (headers as { common?: { "Content-Type"?: string } })
              .common?.["Content-Type"];
          }
          return data;
        },
      ],
    }
  );
  return response.data?.data as { url: string };
};

export const deleteImageGeneratorSpecification = async (url: string) => {
  const response = await admin.delete("image-generator/specification-image", {
    data: { url },
  });
  return response.data?.data as { deleted: boolean };
};
